import React, { useEffect, useState, useCallback } from 'react';
import {
  Button,
  Box,
  CircularProgress,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Paper,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import axios from 'axios';
import { API_BASE_URL } from '../../constants/constants';
import { DataGrid } from '@mui/x-data-grid';
import { format } from 'date-fns';
import BackButton from '../../components/BackButton';
import { usePermissions } from '../../context/PermissionContext';
// [CASH-MODE] non-customer payments are always UPI; Cash filter returns nothing
import PaymentModeFilter from '../../components/PaymentModeFilter';
import PaymentModeChip from '../../components/PaymentModeChip';
import PaymentModeTotals from '../../components/PaymentModeTotals';
import { PAYMENT_MODES } from '../../constants/paymentModes';
import { paymentModeParam, paymentModeLabel, filterRowsByMode, totalsByModeFromRows } from '../../utils/paymentMode';

const NonCustomerCommissionReport = () => {
  const [startDate, setStartDate] = useState(
    new Date(new Date().setDate(new Date().getDate() - 30))
  );
  const [endDate, setEndDate] = useState(new Date());
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');
  const [transactions, setTransactions] = useState([]);
  const [grandTotal, setGrandTotal] = useState(0);
  const [grandTransactionTotal, setGrandTransactionTotal] = useState(0);
  const [grandGstTotal, setGrandGstTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const { can } = usePermissions();
  const [paymentMode, setPaymentMode] = useState(PAYMENT_MODES.ALL); // [CASH-MODE]
  const [totalsByMode, setTotalsByMode] = useState(null); // [CASH-MODE]

  const validateDates = useCallback(() => {
    if (!startDate || !endDate) {
      setDialogMessage('Please select both start date and end date.');
      setOpenDialog(true);
      return false;
    }
    if (startDate > endDate) {
      setDialogMessage('Start date must be on or before the end date.');
      setOpenDialog(true);
      return false;
    }
    return true;
  }, [startDate, endDate]);

  const fetchData = useCallback(async () => {
    if (!validateDates()) return;
    setLoading(true);
    try {
      const formattedStartDate = format(startDate, 'yyyy-MM-dd');
      const formattedEndDate = format(endDate, 'yyyy-MM-dd');
      const token = sessionStorage.getItem('token');

      const response = await axios.post(
        `${API_BASE_URL}/nonCustomerCommission/report`,
        { startDate: formattedStartDate, endDate: formattedEndDate, paymentMode: paymentModeParam(paymentMode) }, // [CASH-MODE]
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200) {
        const data = response.data;
        // [CASH-MODE] client-side guard in case the API ignores the filter
        const isCashOnly = paymentMode === PAYMENT_MODES.CASH;
        const txs = filterRowsByMode(data.transactions || [], paymentMode).map((item, index) => ({
          id: item._id || index,
          serialNumber: item.serialNumber || index + 1,
          dateTime: item.dateTime || 'N/A',
          stateHeadId: item.stateHeadId || 'N/A',
          stateStaffId: item.stateStaffId || 'N/A',
          qpayCentreId: item.qpayCentreId || 'N/A',
          qpayAgentId: item.qpayAgentId || 'N/A',
          shopName: item.shopName || 'N/A',
          merchantId: item.merchantId || 'N/A',
          payerName: item.payerName || 'N/A',
          payerVPA: item.payerVPA || 'N/A',
          transactionAmount: item.transactionAmount || '0.00',
          commissionPercentage: item.commissionPercentage || '0.05',
          commissionAmount: item.commissionAmount || '0.00',
          gstPercentage: item.gstPercentage || '18',
          gstAmount: item.gstAmount || '0.00',
          paymentMode: item.paymentMode || PAYMENT_MODES.UPI, // [CASH-MODE]
        }));
        setTransactions(txs);
        // [CASH-MODE] Cash selected => no non-customer data
        setGrandTotal(isCashOnly ? 0 : data.grandTotal || 0);
        setGrandTransactionTotal(isCashOnly ? 0 : data.grandTransactionTotal || 0);
        setGrandGstTotal(isCashOnly ? 0 : data.grandGstTotal || 0);
        setTotalsByMode(data.totalsByMode || null); // [CASH-MODE]
      } else {
        setTransactions([]);
        setGrandTotal(0);
        setGrandTransactionTotal(0);
        setGrandGstTotal(0);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setTransactions([]);
      setGrandTotal(0);
      setGrandTransactionTotal(0);
      setGrandGstTotal(0);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, validateDates, paymentMode]); // [CASH-MODE] paymentMode

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExportCSV = () => {
    if (!can('reports.nonCustomerCommission.export')) return;
    if (transactions.length === 0) {
      setDialogMessage('No data to export.');
      setOpenDialog(true);
      return;
    }

    const headers = [
      'SL No',
      'Date & Time',
      'State Head ID',
      'State Staff ID',
      'Q-Pay Centre ID',
      'Q-Pay Agent ID',
      'Shop Name',
      'Merchant ID',
      'Payer Name',
      'Payer VPA',
      'Mode', // [CASH-MODE]
      'Transaction Amount',
      'Commission %',
      'Commission Amount',
      'GST %',
      'GST Amount',
    ];

    const rows = transactions.map((t) => [
      t.serialNumber,
      `"${t.dateTime}"`,
      `"${t.stateHeadId}"`,
      `"${t.stateStaffId}"`,
      `"${t.qpayCentreId}"`,
      `"${t.qpayAgentId}"`,
      `"${t.shopName}"`,
      `"${t.merchantId}"`,
      `"${t.payerName}"`,
      `"${t.payerVPA}"`,
      paymentModeLabel(t), // [CASH-MODE]
      t.transactionAmount,
      t.commissionPercentage,
      t.commissionAmount,
      t.gstPercentage,
      t.gstAmount,
    ]);

    const csvContent =
      'data:text/csv;charset=utf-8,' +
      [headers.join(','), ...rows.map((e) => e.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `Non_Customer_Commission_Report_${format(new Date(), 'yyyy-MM-dd')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const columns = [
    { field: 'serialNumber', headerName: 'SL No', width: 70 },
    { field: 'dateTime', headerName: 'Date & Time', width: 170 },
    { field: 'stateHeadId', headerName: 'State Head ID', width: 130 },
    { field: 'stateStaffId', headerName: 'State Staff ID', width: 130 },
    { field: 'qpayCentreId', headerName: 'Q-Pay Centre ID', width: 140 },
    { field: 'qpayAgentId', headerName: 'Q-Pay Agent ID', width: 140 },
    { field: 'shopName', headerName: 'Shop Name', width: 160 },
    { field: 'merchantId', headerName: 'Merchant ID', width: 140 },
    { field: 'payerName', headerName: 'Payer Name', width: 150 },
    { field: 'payerVPA', headerName: 'Payer VPA', width: 200 },
    // [CASH-MODE]
    { field: 'paymentMode', headerName: 'Mode', width: 90, renderCell: (params) => <PaymentModeChip row={params.row} /> },
    { field: 'transactionAmount', headerName: 'Transaction Amount (₹)', width: 180 },
    { field: 'commissionPercentage', headerName: 'Commission %', width: 130 },
    { field: 'commissionAmount', headerName: 'Commission Amount (₹)', width: 180 },
    { field: 'gstPercentage', headerName: 'GST %', width: 90 },
    { field: 'gstAmount', headerName: 'GST Amount (₹)', width: 150 },
  ];

  // [CASH-MODE] prefer API totals, fall back to loaded rows
  const modeTotals = totalsByMode || totalsByModeFromRows(transactions, (r) => r.transactionAmount);
  const modeAmountKey = modeTotals?.UPI?.transactionAmount !== undefined ? 'transactionAmount' : 'amount';

  return (
    <Box sx={{ position: 'relative', padding: 3 }}>
      <BackButton />
      <br /><br />
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Paper elevation={3} sx={{ p: 3, borderRadius: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              Non-Customer Commission Report
            </Typography>

            {can('reports.nonCustomerCommission.export') && (
              <Button
                variant="contained"
                color="success"
                startIcon={<DownloadIcon />}
                onClick={handleExportCSV}
                sx={{ fontWeight: 'bold', borderRadius: 2 }}
              >
                Export CSV
              </Button>
            )}
          </Box>

          <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Grid item xs={12} sm={4}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(date) => setStartDate(date)}
                renderInput={(params) => <TextField fullWidth {...params} />}
                maxDate={new Date()}
                inputFormat="dd-MM-yyyy"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={(date) => setEndDate(date)}
                renderInput={(params) => <TextField fullWidth {...params} />}
                maxDate={new Date()}
                inputFormat="dd-MM-yyyy"
              />
            </Grid>
            {/* [CASH-MODE] */}
            <Grid item xs={12} sm={4}>
              <PaymentModeFilter value={paymentMode} onChange={setPaymentMode} fullWidth />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button
                variant="contained"
                color="primary"
                onClick={fetchData}
                disabled={loading}
                sx={{ py: 1.5, px: 4, fontWeight: 'bold' }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Submit'}
              </Button>
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={3}>
              <Paper elevation={1} sx={{ p: 2, textAlign: 'center', bgcolor: '#f5f5f5' }}>
                <Typography variant="body2" color="textSecondary">Total Transactions Amount</Typography>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>₹{Number(grandTransactionTotal).toFixed(2)}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Paper elevation={1} sx={{ p: 2, textAlign: 'center', bgcolor: '#f5f5f5' }}>
                <Typography variant="body2" color="textSecondary">Total Commission Collected</Typography>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'success.main' }}>₹{Number(grandTotal).toFixed(2)}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Paper elevation={1} sx={{ p: 2, textAlign: 'center', bgcolor: '#f5f5f5' }}>
                <Typography variant="body2" color="textSecondary">Total GST</Typography>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'error.main' }}>₹{Number(grandGstTotal).toFixed(2)}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Paper elevation={1} sx={{ p: 2, textAlign: 'center', bgcolor: '#f5f5f5' }}>
                <Typography variant="body2" color="textSecondary">Total Records</Typography>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{transactions.length}</Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* [CASH-MODE] */}
          <PaymentModeTotals totals={modeTotals} amountKey={modeAmountKey} title="Transactions" sx={{ mb: 2 }} />

          <Box sx={{ height: 500, width: '100%' }}>
            <DataGrid
              rows={transactions}
              columns={columns}
              pageSize={10}
              rowsPerPageOptions={[10, 25, 50, 100]}
              loading={loading}
              disableSelectionOnClick
              rowHeight={60}
            />
          </Box>
        </Paper>

        <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
          <DialogTitle>Notification</DialogTitle>
          <DialogContent>
            <Typography>{dialogMessage}</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)} color="primary">
              OK
            </Button>
          </DialogActions>
        </Dialog>
      </LocalizationProvider>
    </Box>
  );
};

export default NonCustomerCommissionReport;
